import { useEffect } from 'react';

import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { PropTypes } from 'prop-types';

import { useErrorDisplayedCallback } from '@zenbusiness/application-commons-clickstream';
import { useCartContents } from '@zenbusiness/application-commons-ecommerce';
import { STATUS } from '@zenbusiness/application-commons-ecommerce/useSubmitOrder';
import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Divider from '@zenbusiness/zui/Divider';
import Empty from '@zenbusiness/zui/Empty';
import Link from '@zenbusiness/zui/Link';
import Text from '@zenbusiness/zui/Text';

import { FEDERATED } from '#client/build-env';
import CalloutIcon from '#client/components/CalloutIcon';
import usePaymentInfoEnteredProps from '#client/hooks/clickstream/usePaymentInfoEnteredProps';
import useFlowState from '#client/hooks/flow/useFlowState';
import { useCheckoutContext } from '#client/pages/Checkout/hooks/useCheckoutContext';

import { CHECKOUT } from '../../../../constants';
import { CheckoutCard } from '../../../CheckoutCard';
import ListCallout from '../../../ListCallout';

const { ACTION, STEP } = CHECKOUT;

function PaymentCheckoutCard({ onPlaceOrder }) {
  const { formatMessage } = useIntl();
  const [{ cardToken, paymentMethodId, placeOrder }, dispatch] = useCheckoutContext();
  const { contactInfo } = useFlowState();
  const { firstName, lastName } = contactInfo;
  const paymentInfoEnteredProps = usePaymentInfoEnteredProps({ paymentMethod: 'credit_card' });
  const contents = useCartContents();

  const includesSubscription = contents.items?.some(
    (cartItem) => (cartItem.interval || 'onetime') !== 'onetime'
  );
  const dueTodayAmount = contents.totalPrice?.dueToday?.formattedAmount;

  const handlePaymentInstrumentChange = ({
    cardToken: newCardToken,
    paymentMethodId: newPaymentMethodId
  }) => {
    dispatch({
      type: ACTION.SET_PAYMENT_INSTRUMENT,
      payload: { cardToken: newCardToken, paymentMethodId: newPaymentMethodId }
    });
  };

  const isPlaceOrderAllowed = !!cardToken || !!paymentMethodId;
  const hasFatalError = placeOrder.error?.isFatal;
  const isLoading = placeOrder.status === STATUS.PENDING;
  const hasError = placeOrder.status === STATUS.FAILED;

  const errorCode = hasError ? placeOrder.error?.errorCode : null;
  const errorMessage = hasError
    ? formatMessage({
        id: errorCode
          ? `shopping.checkout.error.${errorCode}`
          : 'shopping.page.checkout.error.generic'
      })
    : null;

  const trackErrorDisplayed = useErrorDisplayedCallback({
    elementName: 'place_order_error',
    elementText: errorMessage,
    elementValue: errorCode
  });

  useEffect(() => {
    if (hasError) {
      trackErrorDisplayed();
    }
    // NOTE: The track function is memoized, there is no need to be listed as dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  return (
    <CheckoutCard step={STEP.PAYMENT}>
      <CheckoutCard.Active>
        <FederatedModule
          exportName="OrderSummaryWidget"
          silent
          {...FEDERATED.EcommerceUi}
          loadingElement={<Empty />}
        />
        <Divider sx={{ marginTop: (theme) => theme.spacing(2) }} />
        <Box
          sx={{
            pointerEvents: isLoading ? 'none' : 'auto'
          }}
        >
          <FederatedModule
            clickstreamProps={paymentInfoEnteredProps()}
            exportName="PaymentMethodsWidget"
            silent
            userInfo={{ firstName, lastName }}
            onChange={handlePaymentInstrumentChange}
            {...FEDERATED.EcommerceUi}
            loadingElement={<Empty />}
          />
        </Box>
        <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
        <Box display={{ xs: 'block', md: 'none' }}>
          <ListCallout />
        </Box>
        {includesSubscription && (
          <Text color="tertiary" size="xs">
            <FormattedMessage id="shopping.page.checkout.payment.renewal" />
          </Text>
        )}
        {hasError && (
          <CalloutIcon
            color="red"
            description={errorMessage}
            icon={<ErrorOutlineRoundedIcon />}
            iconBackgroundColor="red"
          />
        )}
        {!hasFatalError && (
          <Button
            clickstreamProps={{
              elementName: 'checkout_card_place_order',
              elementValue: STEP.PAYMENT
            }}
            data-testid={`checkout-card-place-order-${STEP.PAYMENT}`}
            disabled={!isPlaceOrderAllowed}
            fullWidth
            loading={isLoading}
            onClick={onPlaceOrder}
          >
            <FormattedMessage
              id="shopping.page.checkout.payment.placeOrder"
              values={{ dueTodayAmount }}
            />
          </Button>
        )}
        <Text size="xs">
          <FormattedMessage
            id="shopping.page.checkout.payment.agreement"
            values={{
              terms: (
                <Link
                  href="https://www.zenbusiness.com/support-terms/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FormattedMessage id="shopping.page.checkout.payment.terms" />
                </Link>
              ),
              privacy: (
                <Link
                  href="https://www.zenbusiness.com/support-privacy-policy/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FormattedMessage id="shopping.page.checkout.payment.privacy" />
                </Link>
              )
            }}
          />
        </Text>
      </CheckoutCard.Active>
      <CheckoutCard.Complete />
      <CheckoutCard.Incomplete />
    </CheckoutCard>
  );
}

PaymentCheckoutCard.propTypes = {
  onPlaceOrder: PropTypes.func.isRequired
};

export default PaymentCheckoutCard;
